import React, { useState, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IonContent, IonPage, IonItem, IonLabel, IonButton, IonInput, IonHeader, IonFooter, IonLoading, 
        IonGrid, IonCol, IonRow, IonImg, IonAlert, IonSelect, IonSelectOption, IonTitle, IonToast } from '@ionic/react';
import { EnumSecurityObject, EnumSecurityAction, EnumSecurityLevel } from '../domain/enums'; 
import { IClient, IClientList } from '../domain/Client';
import { Plugins } from '@capacitor/core';
import { appVersion, authenticateUserAsync, getAuthorizedClients, getServerVersion, simpleAppVersion } from '../services/asyncDataService';
import { loadLists } from '../services/utils';

import './css/Login.css';

const { Network } = Plugins;

const Login: React.FC<RouteComponentProps> = ({match, history, location}) => { 
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showLoading, setShowLoading] = useState(false);
    const [showPopupRights, setShowPopupRights] = useState(false);
    const [showPopupNoConnection, setShowPopupNoConnection] = useState(false);
    const [showWrongCredentials, setShowWrongCredentials] = useState(false);
    const [showMissingClient, setShowMissingClient] = useState(false);
    const [isVisible, setIsVisisble] = useState(false);
    const [versionNotification, setVersionNotification] = useState(false);
    const [harbor, setHarbor] = useState("-1");
    const [harbors, setHarbors] = useState<IClient[]>([]);
    const [, setIsOnline] = useState<boolean>(window.localStorage.getItem("online") !== "0");

    useEffect(() => {
        loadAndVerify();
        Network.addListener("networkStatusChange", status => {
          setIsOnline(status.connected);
          if (status.connected)
            window.localStorage.setItem("online", "1");
          else
            window.localStorage.setItem("online", "0");
        });
    
        return () => Network.removeAllListeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    const loadAndVerify = async() => {
        let isNewVersion = false;
        let version = await checkVersion();

        if (version !== simpleAppVersion) 
            isNewVersion = true;
    
        onLoginClick(true, isNewVersion);
    }

    const clientList = useMemo(() => {
        let clientListToReturn: any[] = [];
        if (harbors.length > 0) { 
            let clientListSorted = harbors.filter((c:any) => c.ClientID !== -1)
                                    .sort((a:any,b:any) => { if (a.ClientName < b.ClientName) return -1;
                                        if (a.ClientName > b.ClientName) return 1;
                                        return 0;
                                      });
            clientListToReturn = clientListSorted.map((client:any) => ({ 
                  id: client.ClientID?.toString(), 
                  name: client.ClientName     
                }));
            setHarbor(clientListToReturn[0] ? clientListToReturn[0].id : -1);
        }
        return clientListToReturn;
    }, [harbors]);

    const loadClientListAsync = async(token:string|null) => {
        const expectedData = await getAuthorizedClients(String(token),
                    EnumSecurityObject.Berth.toString(),
                    EnumSecurityAction.View.toString(),
                    EnumSecurityLevel.Filtered.toString());
        if (expectedData) {
            let clients = (JSON.parse(expectedData.toString()) as IClientList).Clients;
            window.localStorage.setItem("clients", JSON.stringify(clients));
            setHarbors(clients);
            if (clients.length === 0) {
                window.localStorage.setItem("defaultClient", "-1");
                return [];
            }
            else {
                let lClientId = clients[0].ClientID === -1 ? clients[1].ClientID.toString() : clients[0].ClientID.toString() ;
                let lClientName = clients[0].ClientID === -1 ? clients[1].ClientName.toString() : clients[0].ClientName.toString();
                window.localStorage.setItem("defaultClient", lClientId);
                window.localStorage.setItem("defaultClientName", lClientName);
                
                return clients; 
            }
        }
        else {
            return [];
        }
    }

    const setLoginStorage = (token:string, expirationDate: string, username:string, password:string) => {
        window.localStorage.setItem("token", token);
        window.localStorage.setItem("expireTokenDate", expirationDate);
        window.localStorage.setItem("username", username);
        window.localStorage.setItem("password", password);
    }

    const changeHarbor = (e:any) => { 
        setHarbor(e.detail.value);
        window.localStorage.setItem("defaultClient", e.detail.value.toString());
        let _clients = JSON.parse(String(window.localStorage.getItem("clients"))) as IClient[];
        let filteredClient = _clients.find((x) => x.ClientID.toString() === e.detail.value.toString());
        if (filteredClient){
            window.localStorage.setItem("defaultClientName", filteredClient.ClientName.toString());
        }  
    }

    const onLoginClick = (onLoad:boolean, isNewVersion: boolean = false) => {
        if (onLoad) {
            if (window.localStorage.getItem("username") && window.localStorage.getItem("password") 
                    && window.localStorage.getItem("token") && window.localStorage.getItem("expireTokenDate")
                    && new Date(String(window.localStorage.getItem("expireTokenDate"))) > new Date()
                    && !isNewVersion
                    && window.localStorage.getItem("defaultClient") 
                    && String(window.localStorage.getItem("defaultClient")) !== "-1") {
                loadLists(window.localStorage.getItem("token"), String(window.localStorage.getItem("username")));
                history.push('/home');  
            } 
            else if (isNewVersion) {
                setVersionNotification(true);
            }                      
        }
        else if (isVisible && String(window.localStorage.getItem("defaultClient")) !== "-1")
            login();
        else
            loginProcess();
    }

    const checkVersion = async() => {
        let serverVersion = await getServerVersion();
        return serverVersion;
    }

    const login = async() => {
        if (window.localStorage.getItem("username") 
                && window.localStorage.getItem("password") 
                && window.localStorage.getItem("token")
                && window.localStorage.getItem("expireTokenDate")
                && new Date(String(window.localStorage.getItem("expireTokenDate"))) > new Date()) {
            if (window.localStorage.getItem("defaultClient") && String(window.localStorage.getItem("defaultClient")) !== "-1") {
                loadLists(window.localStorage.getItem("token"), String(window.localStorage.getItem("username")));
                history.push('/home');
            }
            else 
                setShowMissingClient(true);
        }
        else
            setShowPopupRights(true);
    }

    const loginProcess = async() => {
        if (isVisible)
            return;
        let data = {
            username: username,
            password: password,
            grant_type: "password"
        };
        setShowLoading(true);
        if (username === String(window.localStorage.getItem("username")) && 
            password === String(window.localStorage.getItem("password"))) {
            if (window.localStorage.getItem("token") && window.localStorage.getItem("expireTokenDate")) {
                let storedToken = window.localStorage.getItem("token");
                let expireTokenDate = new Date(String(window.localStorage.getItem("expireTokenDate")));
                if (expireTokenDate < new Date()) {
                    let expectedData = await authenticateUserAsync(data);
                    if (expectedData && (expectedData as any).access_token) {
                        let myToken = (expectedData as any).access_token;
                        let expirationDate = (expectedData as any)[".expires"];
                        setLoginStorage(myToken, expirationDate, data.username, data.password);
                        try {
                            let _returnedClients = await loadClientListAsync(myToken);
                            setIsVisisble(true);
                            setShowLoading(false);
                            if (_returnedClients.length === 1) {
                                login();
                            }
                        }
                        catch (ex) {
                            setShowLoading(false);
                            setShowPopupRights(true);
                        }
                    }
                    else if ((expectedData as any)?.error === "invalid_grant"){
                        window.localStorage.setItem("defaultClient", "-1");
                        setShowLoading(false);
                        setShowWrongCredentials(true);
                    }
                    else {
                        window.localStorage.setItem("defaultClient", "-1");
                        setShowLoading(false);
                        setShowPopupNoConnection(true);
                    }
                } 
                else {
                    try {
                        let _returnedClients = await loadClientListAsync(storedToken);
                        setIsVisisble(true);
                        setShowLoading(false);
                        if (_returnedClients.length === 1) {
                            login();
                        }
                    }
                    catch (ex) {
                        setShowLoading(false);
                        setShowPopupRights(true);
                    }
                }
            }
            else {
                let expectedData = await authenticateUserAsync(data);
                if (expectedData && (expectedData as any).access_token) {
                    let myToken = (expectedData as any).access_token;
                    let expirationDate = (expectedData as any)[".expires"];
                    setLoginStorage(myToken, expirationDate, data.username, data.password);
                    try {
                        let _returnedClients = await loadClientListAsync(myToken);
                        setIsVisisble(true);
                        setShowLoading(false);
                        if (_returnedClients.length === 1) {
                            login();
                        }
                    }
                    catch (ex) {
                        setShowLoading(false);
                        setShowPopupRights(true);
                    }
                }
                else if ((expectedData as any)?.error === "invalid_grant"){
                    window.localStorage.setItem("defaultClient", "-1");
                    setShowLoading(false);
                    setShowWrongCredentials(true);
                }
                else {
                    window.localStorage.setItem("defaultClient", "-1");
                    setShowLoading(false);
                    setShowPopupNoConnection(true);
                }
            }
        }
        else { 
            let expectedData = await authenticateUserAsync(data);
            if (expectedData && (expectedData as any).access_token) {
                let myToken = (expectedData as any).access_token;
                let expirationDate = (expectedData as any)[".expires"];
                setLoginStorage(myToken, expirationDate, data.username, data.password);
                try {
                    let _returnedClients = await loadClientListAsync(myToken);
                    setIsVisisble(true);
                    setShowLoading(false);
                    if (_returnedClients.length === 1) 
                        login();
                        
                }
                catch (ex) {
                    setShowLoading(false);
                    setShowPopupRights(true);
                }
            }
            else if ((expectedData as any)?.error === "invalid_grant") {
                window.localStorage.setItem("defaultClient", "-1");
                setShowLoading(false);
                setShowWrongCredentials(true);
            }
            else {
                window.localStorage.setItem("defaultClient", "-1");
                setShowLoading(false);
                setShowPopupNoConnection(true);
            }
        }
    }
    
    return (
        <IonPage>
            <IonHeader mode="ios"></IonHeader>  
            <IonContent class="login-ion-content">
                <IonGrid>
                    <IonRow class="my-first-row">
                        <IonCol></IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol size="1" size-sm></IonCol>
                        <IonCol>
                            <IonImg src={"assets/images/logoBigNoBack.png"} />
                        </IonCol>
                        <IonCol size="1" size-sm></IonCol>
                    </IonRow>
                    <IonRow class="ion-justify-content-center">
                        <IonCol class="ion-align-self-center">
                            <IonTitle class="my-login-subtitle">ACTIVITY LOG</IonTitle>
                        </IonCol>
                    </IonRow>
                    <IonRow class="ion-justify-content-center">
                        <IonCol class="ion-align-self-center">
                            <IonTitle size="small" class="my-login-subtitle">Version: {appVersion}</IonTitle>
                        </IonCol>
                    </IonRow>
                    <IonRow class="my-third-row">
                        <IonCol></IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol size="1" size-sm></IonCol>
                        <IonCol>
                            <IonItem mode="ios" class="my-login-ion-input">
                                <IonLabel mode="ios" class="my-login-label" position="stacked">LoginID</IonLabel>
                                <IonInput mode="ios" class="my-login-input" value={username}
                                    placeholder="Enter your LoginID"
                                    onIonChange={(e) => setUsername(e.detail.value!)}></IonInput>
                            </IonItem>
                        </IonCol>
                        <IonCol size="1" size-sm></IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol size="1" size-sm></IonCol>
                        <IonCol>
                            <IonItem mode="ios" class="my-login-ion-input">
                                <IonLabel mode="ios" class="my-login-label" position="stacked">Password</IonLabel>
                                <IonInput mode="ios" value={password} type="password" 
                                    placeholder="Enter your password" 
                                    onIonChange={(e) => setPassword(e.detail.value!)}>
                                </IonInput>
                            </IonItem>
                        </IonCol>
                        <IonCol size="1" size-sm></IonCol>
                    </IonRow>
                    {clientList.length > 1 &&
                        <IonRow>
                            <IonCol size="1" size-sm></IonCol>
                            <IonCol>
                                <IonItem mode="ios" className="my-login-ion-input">
                                    <IonLabel mode="ios" class="my-login-label" position="stacked">Harbor</IonLabel>
                                    <IonSelect value={harbor} placeholder="Select Harbor" interface="action-sheet" 
                                        mode="ios" onIonChange={e => changeHarbor(e)}>
                                            {clientList.map((x:any) => {
                                                return <IonSelectOption className="my-select-option" key={x.id} value={x.id}>{x.name}</IonSelectOption>
                                            })}  
                                    </IonSelect>
                                </IonItem>
                            </IonCol>
                            <IonCol size="1" size-sm></IonCol>
                        </IonRow> 
                    }
                    <IonRow>
                        <IonCol size="1" size-sm></IonCol>
                        <IonCol className="ion-align-self-center">
                            <IonButton mode="ios" class="my-login-button" onClick={() => onLoginClick(false)}>Login</IonButton>
                        </IonCol>
                        <IonCol size="1" size-sm></IonCol>
                    </IonRow> 
                </IonGrid>
                <IonLoading mode="ios"
                    cssClass='my-loading'
                    isOpen={showLoading}
                    onDidDismiss={() => setShowLoading(false)}
                    message={'Getting data...'}
                /> 
                <IonAlert mode="ios"
                    isOpen={showWrongCredentials}
                    onDidDismiss={() => setShowWrongCredentials(false)}
                    cssClass='my-custom-class'
                    header={'Alert'}
                    subHeader={''}
                    message={'Username or password is not correct.'}
                    buttons={['OK']}
                />   
                <IonAlert mode="ios"
                    isOpen={showPopupRights}
                    onDidDismiss={() => setShowPopupRights(false)}
                    cssClass='my-custom-class'
                    header={'Alert'}
                    subHeader={''}
                    message={'You do not have rights to use this app.'}
                    buttons={['OK']}
                /> 
                <IonAlert mode="ios"
                    isOpen={showPopupNoConnection}
                    onDidDismiss={() => setShowPopupNoConnection(false)}
                    cssClass='my-custom-class'
                    header={'Alert'}
                    subHeader={''}
                    message={'You do not have a connection to the database.'}
                    buttons={['OK']}
                /> 
                <IonAlert mode="ios"
                    isOpen={showMissingClient}
                    onDidDismiss={() => setShowMissingClient(false)}
                    cssClass='my-custom-class'
                    header={'Alert'}
                    subHeader={''}
                    message={'Please select a harbor'}
                    buttons={['OK']}
                /> 
                {/* <IonToast mode="ios"
                    isOpen={appState.versionNotification}
                    onDidDismiss={() => setAppState({...appState, ...{versionNotification: false}})}
                    message = {"There is a new version available"}
                    position="top"
                    cssClass="my-ion-toast"
                    color='primary'
                    duration={4000} 
                /> */}
            </IonContent>
            <IonFooter mode="ios"></IonFooter>
            <IonToast mode="ios"
                isOpen={versionNotification}
                onDidDismiss={() => setVersionNotification(false)}
                message = {"There is a new version available"}
                position="top"
                cssClass="my-ion-toast"
                color='success'
                duration={5000}
            />
        </IonPage>
    )
};

export default Login;
  